import { FilterField } from "@/components/filters/filter-field";
import { ItemListSectionHeaderRow } from "@/components/item-list/rows/variants/item-list-section-header-row";
import { ItemListRowType } from "@/components/item-list/types";
import { Hotkey } from "@tanstack/react-hotkeys";
import {
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSFadeBottom,
  MDSMenuItem,
  MDSMenuItemVariant,
  MDSModalDialog,
  MDSShortcutKeyCombo,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import {
  StyledShortcutKeyText,
  StyledShortcutKeyWrapper,
} from "@/mds/components/shortcut-key/shortcut-key.style";
import { useScrollbarState } from "@/domains/react/useScrollbarState";
import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveHotkey } from "./platform";
import {
  getKeyboardShortcutModalSections,
  KeyboardShortcutDisplayBinding,
  KeyboardShortcutDisplaySuffixPart,
} from "./registry";

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  all?: boolean;
}

export function KeyboardShortcutsModal({
  all = false,
  isOpen,
  onClose,
}: KeyboardShortcutsModalProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState("");
  const [shouldAnimateShortcutListFade, setShouldAnimateShortcutListFade] = useState(false);
  const { contentRef, hasScrollableContentBelow } = useScrollbarState({
    initialHasScrollableContentBelow: true,
    isEnabled: isOpen,
  });
  const shortcutSections = getKeyboardShortcutModalSections({ all });
  const handleClose = useCallback(() => {
    setSearchQuery("");
    onClose();
  }, [onClose]);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredShortcutSections = useMemo(
    () =>
      shortcutSections.flatMap(section => {
        const filteredShortcuts = section.shortcuts.filter(shortcut => {
          const searchableTerms = [
            shortcut.label,
            shortcut.contextLabel ?? "",
            shortcut.searchHint ?? "",
          ];
          return searchableTerms.some(term =>
            term.trim().toLowerCase().includes(normalizedSearchQuery)
          );
        });
        if (filteredShortcuts.length === 0) {
          return [];
        }

        return [{ ...section, shortcuts: filteredShortcuts }];
      }),
    [normalizedSearchQuery, shortcutSections]
  );

  useEffect(() => {
    if (!isOpen) return;

    requestAnimationFrame(() => {
      contentRef.current?.parentElement?.scrollTo({ top: 0 });
    });
  }, [contentRef, isOpen]);

  useEffect(() => {
    if (isOpen) return;

    setSearchQuery("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShouldAnimateShortcutListFade(false);
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      setShouldAnimateShortcutListFade(true);
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [isOpen]);

  return (
    <StyledKeyboardShortcutsDialog isOpen={isOpen} handleClose={handleClose}>
      <StyledHeader slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Keyboard shortcuts
      </StyledHeader>
      <StyledDialogTextContent slot={MDSDialogSlotName.Content}>
        <FilterContainer>
          <FilterField
            placeholder="Search shortcuts"
            value={searchQuery}
            onChange={setSearchQuery}
            onClearText={() => setSearchQuery("")}
            showClearTextButton={true}
            autoFocus={true}
            inputProps={{
              onKeyDown: event => {
                if (event.code === "Escape") {
                  event.preventDefault();
                  handleClose();
                  return;
                }

                if (event.code !== "ArrowDown" && event.code !== "ArrowUp") {
                  return;
                }

                contentRef.current?.parentElement?.scrollBy({
                  top: event.code === "ArrowDown" ? 48 : -48,
                  behavior: "auto",
                });
                event.preventDefault();
              },
            }}
          />
        </FilterContainer>
        <ShortcutListWrapper>
          <ShortcutListScrollContainer data-test-id="keyboard-shortcuts-list-scroll-container">
            <ShortcutListContent ref={contentRef}>
              {filteredShortcutSections.map(section => (
                <SectionContainer key={section.section}>
                  <PaddedHeader
                    data={{
                      type: ItemListRowType.SectionHeader,
                      key: section.section,
                      payload: { title: section.title },
                    }}
                  />
                  {section.shortcuts.map(shortcut => (
                    <UnpaddedMenuItem
                      key={shortcut.id}
                      id={shortcut.id}
                      isInert={true}
                      label={shortcut.label}
                      variant={MDSMenuItemVariant.OneLine}
                      rightAddon={() => (
                        <ShortcutDisplayBindings bindings={shortcut.displayBindings} />
                      )}
                    />
                  ))}
                </SectionContainer>
              ))}
            </ShortcutListContent>
          </ShortcutListScrollContainer>
          <ShortcutListFade
            animateOpacity={shouldAnimateShortcutListFade}
            visible={hasScrollableContentBelow}
          />
        </ShortcutListWrapper>
      </StyledDialogTextContent>
    </StyledKeyboardShortcutsDialog>
  );
}

const StyledKeyboardShortcutsDialog = styled(MDSModalDialog)`
  .mds-dialog {
    height: 60dvh;
    max-height: 60dvh;
    padding-right: ${({ theme }) => theme.spacing["5"]};
  }

  [data-mds-name="icon-button"][aria-label="Close"] {
    margin-right: ${({ theme }) => theme.spacing["4"]};
  }

  [data-mds-name="dialog-body"] {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  @media (max-width: 640px) {
    .mds-dialog {
      height: 90dvh;
      max-height: 90dvh;
    }
  }
`;

const StyledDialogTextContent = styled(MDSDialogTextContent)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 0;
`;

const ShortcutListWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`;

const ShortcutListScrollContainer = styled.div`
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding-right: ${({ theme }) => theme.spacing["4"]};
`;

const ShortcutListContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShortcutListFade = styled(MDSFadeBottom)`
  height: ${({ theme }) => `calc(${theme.spacing["8"]} * 2)`};
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.surface.modal}00,
    ${({ theme }) => theme.colors.surface.modal}
  );
`;

const FilterContainer = styled.div`
  padding: ${({ theme }) => theme.spacing["2"]} 0;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.surface.modal};
  padding-right: ${({ theme }) => theme.spacing["4"]};
`;

const PaddedHeader = styled(ItemListSectionHeaderRow)`
  padding-left: 0;
  padding-top: calc(${({ theme }) => theme.spacing["4"]} + 10px);
  padding-right: ${({ theme }) => theme.spacing["7"]};
  padding-bottom: 10px;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnpaddedMenuItem = styled(MDSMenuItem)`
  padding-left: 0px;
  padding-right: 0px;
`;

const ShortcutDisplayBindings = ({
  bindings,
}: {
  bindings: readonly KeyboardShortcutDisplayBinding[];
}) => (
  <ShortcutBindingsContainer>
    {bindings.map((displayBinding, index) => (
      <ShortcutBindingFragment
        key={`${resolveHotkey(displayBinding.binding)}-${index}`}
        displayBinding={displayBinding}
        showSeparator={index > 0}
      />
    ))}
  </ShortcutBindingsContainer>
);

const ShortcutBindingsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const ShortcutBindingFragment = ({
  displayBinding,
  showSeparator,
}: {
  displayBinding: KeyboardShortcutDisplayBinding;
  showSeparator: boolean;
}) => (
  <>
    {showSeparator && (
      <ShortcutSeparator variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
        or
      </ShortcutSeparator>
    )}
    <ShortcutKeyComboWithSuffix
      hotkey={resolveHotkey(displayBinding.binding)}
      suffixParts={displayBinding.suffixParts}
    />
  </>
);

const ShortcutSeparator = styled(MDSText)`
  padding: ${({ theme }) => `${theme.spacing["0.5"]} ${theme.spacing["1"]}`};
  white-space: nowrap;
`;

const ShortcutKeyComboWithSuffix = ({
  hotkey,
  suffixParts,
}: {
  hotkey: Hotkey;
  suffixParts?: readonly KeyboardShortcutDisplaySuffixPart[];
}) => {
  return (
    <ShortcutKeyComboContainer>
      <MDSShortcutKeyCombo hotkey={hotkey} size="Large" />
      {suffixParts?.map((part, index) =>
        part.kind === "text" ? (
          <ShortcutSuffixText
            key={`${hotkey}-suffix-text-${part.label}-${index}`}
            variant={MDSTextVariant.TextM}
          >
            {part.label}
          </ShortcutSuffixText>
        ) : (
          <ShortcutTokenKeyCap
            key={`${hotkey}-suffix-key-${part.label}-${index}`}
            $size="Large"
            $isFixedWidth={part.label.length === 1}
            $borderless={part.borderless}
          >
            <ShortcutTokenText $size="Large" variant={MDSTextVariant.TextM}>
              {part.label}
            </ShortcutTokenText>
          </ShortcutTokenKeyCap>
        )
      )}
    </ShortcutKeyComboContainer>
  );
};

const ShortcutKeyComboContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const ShortcutTokenKeyCap = styled(StyledShortcutKeyWrapper)<{ $borderless?: boolean }>`
  border: ${({ $borderless }) => ($borderless ? "none" : undefined)};
  white-space: nowrap;
`;

const ShortcutSuffixText = styled(MDSText)`
  white-space: nowrap;
`;

const ShortcutTokenText = styled(StyledShortcutKeyText)`
  white-space: nowrap;
`;

const StyledHeader = styled(MDSText)`
  &[slot="header"] {
    padding: 0;
  }
`;
